import { useEffect } from 'react';
import { Circle, CircleMarker, MapContainer, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { latLngBounds } from 'leaflet';

export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  accuracy?: number | null;
  color: string;
  title: string;
  lines?: string[];
}
export interface MapCenter {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  radiusMeters: number;
}

function Fit({ points, centers }: { points: MapPoint[]; centers: MapCenter[] }) {
  const map = useMap();
  useEffect(() => {
    const all = [...points.map((p) => [p.lat, p.lng] as [number, number]), ...centers.filter((c) => c.lat != null && c.lng != null).map((c) => [c.lat!, c.lng!] as [number, number])];
    if (all.length === 1) map.setView(all[0]!, 16);
    else if (all.length > 1) map.fitBounds(latLngBounds(all), { padding: [30, 30], maxZoom: 17 });
  }, [map, points, centers]);
  return null;
}

/**
 * Mapa de ubicaciones de FICHAJES y verificaciones puntuales (nunca de seguimiento).
 * Muestra el punto, el círculo de precisión y el radio del centro.
 */
export function ClockMap({ points, centers, height = 360 }: { points: MapPoint[]; centers: MapCenter[]; height?: number }) {
  const tile = import.meta.env.VITE_MAP_TILE_URL ?? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  return (
    <div style={{ height }} className="overflow-hidden rounded-xl border border-slate-200">
      <MapContainer center={[40.4168, -3.7038]} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        {/* OpenStreetMap exige saber qué web pide los mapas: solo se envía el dominio (la página ya no envía referente). */}
        <TileLayer url={tile} attribution="© OpenStreetMap contributors" referrerPolicy="strict-origin" />
        {centers
          .filter((c) => c.lat != null && c.lng != null)
          .map((c) => (
            <Circle key={c.id} center={[c.lat!, c.lng!]} radius={c.radiusMeters} pathOptions={{ color: '#1d4ed8', weight: 1.5, fillOpacity: 0.08, dashArray: '4 4' }}>
              <Tooltip>{`${c.name} · radio ${c.radiusMeters} m`}</Tooltip>
            </Circle>
          ))}
        {points.map((p) => (
          <span key={p.id}>
            {p.accuracy ? <Circle center={[p.lat, p.lng]} radius={p.accuracy} pathOptions={{ color: p.color, weight: 0, fillOpacity: 0.12 }} /> : null}
            <CircleMarker center={[p.lat, p.lng]} radius={7} pathOptions={{ color: '#fff', weight: 2, fillColor: p.color, fillOpacity: 1 }}>
              <Popup>
                <strong>{p.title}</strong>
                {p.lines?.map((l) => (
                  <div key={l}>{l}</div>
                ))}
              </Popup>
            </CircleMarker>
          </span>
        ))}
        <Fit points={points} centers={centers} />
      </MapContainer>
    </div>
  );
}
